import type { FC } from 'react';

import DeleteIcon from '@/assets/svg/ui-elements/delete-outline.svg';
import DownloadIcon from '@/assets/svg/ui-elements/download.svg';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import ImageIcon from '@/assets/svg/ui-elements/image-outline.svg';
import { formatFileSize } from '@/utils/format-file-size';

import { IconButton } from '../Button';
import Helper from '../Helper';
import { Progress } from '../Progress';
import { FileTypeIcon } from './file-icon';
import {
  StyledActions,
  StyledFileInfo,
  StyledFileMeta,
  StyledFileName,
  StyledFileNameButton,
  StyledFileRow,
  StyledImagePreview,
  StyledPreviewImage,
} from './FileUpload.styled';
import type { FileItemProps, FileUploadItem, FileUploadVariant } from './types';

const metaLabel = (file: FileUploadItem, variant: FileUploadVariant): string => {
  const size = file.size != null ? formatFileSize(file.size) : null;
  if (variant !== 'image') return size ?? '';

  const extension = file.name.split('.').pop();
  const subtype = file.contentType?.split(';')[0].split('/')[1];
  const format = (extension && extension !== file.name ? extension : subtype)?.toUpperCase();

  return [format, size].filter(Boolean).join(' · ');
};

const RowIcon: FC<{ file: FileUploadItem; variant: FileUploadVariant }> = ({ file, variant }) =>
  variant === 'image' ? (
    <StyledImagePreview>
      {file.previewUrl ? <StyledPreviewImage src={file.previewUrl} alt="" /> : <ImageIcon />}
    </StyledImagePreview>
  ) : (
    <FileTypeIcon fileName={file.name} contentType={file.contentType} />
  );

export const FileItem: FC<FileItemProps> = ({
  file,
  variant = 'file',
  onRemove,
  onDownload,
  onReplace,
  onOpen,
}) => {
  const meta = metaLabel(file, variant);

  return (
    <StyledFileRow $status={file.status}>
      <RowIcon file={file} variant={variant} />

      <StyledFileInfo $inline={file.status === 'uploaded'}>
        {onOpen ? (
          <StyledFileNameButton
            type="button"
            title={file.name}
            aria-label={`Open ${file.name}`}
            onClick={() => onOpen(file)}
          >
            {file.name}
          </StyledFileNameButton>
        ) : (
          <StyledFileName title={file.name}>{file.name}</StyledFileName>
        )}

        {file.status === 'uploading' && (
          <Progress direction="horizontal" value={file.progress ?? null} showProgressLabel />
        )}

        {file.status === 'uploaded' && meta && <StyledFileMeta>{meta}</StyledFileMeta>}

        {file.status === 'error' && (
          <Helper state="destructive" label={file.errorMessage ?? 'Upload failed.'} />
        )}
      </StyledFileInfo>

      {file.status !== 'uploading' && (onDownload || onReplace || onRemove) && (
        <StyledActions>
          {onDownload && file.status === 'uploaded' && (
            <IconButton
              color="tertiary"
              variant="outline"
              size="xs"
              onClick={() => onDownload(file)}
              aria-label={`Download ${file.name}`}
            >
              <DownloadIcon />
            </IconButton>
          )}
          {onReplace && (
            <IconButton
              color="tertiary"
              variant="outline"
              size="xs"
              onClick={() => onReplace(file)}
              aria-label={`Replace ${file.name}`}
            >
              <EditIcon />
            </IconButton>
          )}
          {onRemove && (
            <IconButton
              color="destructive"
              variant="outline"
              size="xs"
              onClick={() => onRemove(file)}
              aria-label={`Remove ${file.name}`}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </StyledActions>
      )}
    </StyledFileRow>
  );
};

FileItem.displayName = 'FileItem';
